import React, { useRef, useState, useMemo } from "react";

import { Typography } from "@packages/ds";
import { ZoomTransform } from "d3";
import { useIntl } from "react-intl";
import { useLocation } from "react-router-dom";

import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import getUrl from "Libs/getUrl";
import { TotalResources } from "Reducers/environment/deployment";
import { gitProjectSelector } from "Reducers/project/project";
import {
  getNodeDimensions,
  nodeRenderer
} from "src/organization/common/containers/ServicesDisplay/util/serviceGraph";
import ConfigureResourcesButton from "src/organization/pages/billing/projectBilling/components/ConfigureResourcesButton";

import DetailDialog from "../DetailDialog";
import Tooltip from "../Tooltip";

import ServiceTree, { ServiceTreeEventPayload } from "./ServiceTree";
import * as S from "./styles";

type Metadata = {
  node?: ServiceTreeEventPayload;
  visible: boolean;
  position?: string;
};

export interface ServiceGraphProps {
  goToService: (metadata: unknown) => void;
  hasInfoTooltip?: boolean;
  onClick: (metadata: ServiceTreeEventPayload) => void;
  onLoadEnd?: () => void;
  strokeColor?: string;
  width?: number | string;
  isSizingApiEnabled?: boolean;
  resources?: TotalResources;
}

function ServiceGraph({
  goToService,
  hasInfoTooltip,
  onClick,
  strokeColor,
  width,
  onLoadEnd,
  isSizingApiEnabled,
  resources
}: ServiceGraphProps) {
  const intl = useIntl();
  const location = useLocation();
  const { organizationId, projectId, environmentId } = useDecodedParams<{
    organizationId: string;
    projectId: string;
    environmentId?: string;
  }>();

  const project = useSelectorWithUrlParams(gitProjectSelector);

  const [tooltipMetadata, setTooltipMetadata] = useState<Metadata>({
    visible: false
  });
  const [dialogMetadata, setDialogMetadata] = useState<Metadata>({
    visible: false
  });
  const [lastNode, setLastNode] = useState<HTMLElement | null>();

  const innerRef = useRef(null);

  const leftOffset = 0;
  const graphHeight = 220;

  const onNodeOver =
    (scale: number, transform: ZoomTransform) =>
    (e: CustomEvent<ServiceTreeEventPayload>) => {
      // If the dialog is visible, we don't want to show the tooltip
      if (dialogMetadata?.visible) {
        return false;
      }
      const leftGap = 16; // Space between the start of the div and the arrow
      const tX = e.detail.x * scale + transform.x - leftGap - leftOffset;

      // The tooltip div start on the bottom of the service tree div
      const tY =
        -graphHeight + // We subtract the service tree div height
        transform.y + // Add the translation
        e.detail.y * scale + // to the current position (* the calculated scale)
        e.detail.size * scale - // + all the node size (* the calculated scale)
        2; // To be closer to the node

      setTooltipMetadata({
        node: e.detail,
        position: `translate(${tX}px, ${tY}px)`,
        visible: true
      });
    };

  const onNodeOut = () => () => {
    setTooltipMetadata({
      ...tooltipMetadata,
      visible: false
    });
  };

  const insertDetailDialog = (
    e: CustomEvent<ServiceTreeEventPayload>,
    scale: number,
    transform: ZoomTransform
  ) => {
    const leftGap = 15; // Space between the start of the div and the arrow 5% * ~277

    Array.from(
      (e.target as HTMLElement | null)?.parentElement?.children ?? []
    ).forEach(node => node.setAttribute("aria-expanded", "false"));

    (e.target as HTMLElement | null)?.setAttribute("aria-expanded", "true");

    const tX = e.detail.x * scale + transform.x - leftGap - leftOffset;
    const tY =
      -graphHeight + // We subtract the service tree div height
      transform.y + // Add the translation
      e.detail.y * scale + // to the current position (* the calculated scale)
      e.detail.size * scale - // + all the node size (* the calculated scale)
      2; // To be closer to the node

    setDialogMetadata({
      node: e.detail,
      position: `translate(${tX}px, ${tY}px)`,
      visible: true
    });

    // Check if we need to resize window to display correctly the dialog
    // const rect = innerRef.current?.getBoundingClientRect();
    // if (rect.bottom + 150 > window.innerHeight) sendFooterEvent(150);
  };

  const closeDetailDialog = () => {
    // sendFooterEvent(0);

    Array.from(document.querySelectorAll("svg .nodes .node")).forEach(node =>
      node.setAttribute("aria-expanded", "")
    );

    lastNode?.focus();
    setDialogMetadata({
      visible: false
    });
  };

  const onTab = (e: KeyboardEvent) => {
    if (e.key !== "Tab") return;

    const isTab = e.key === "Tab" && !e.shiftKey;
    const isSTab = e.key === "Tab" && e.shiftKey;
    const isNode = document.activeElement?.nodeName === "g";

    if (dialogMetadata.visible && isTab && isNode) {
      setLastNode(document.activeElement as HTMLElement);
      e.preventDefault();
      (
        document.querySelector("#service-tree+div .close") as HTMLElement
      )?.focus();
    }

    if (dialogMetadata.visible && !isNode) {
      const focusableNodes = document.querySelectorAll(
        "#service-tree+div [tabindex]"
      );

      if (
        (isSTab && document.activeElement?.isSameNode(focusableNodes[0])) ||
        (isTab &&
          document.activeElement?.isSameNode(
            focusableNodes[focusableNodes.length - 1]
          ))
      ) {
        e.preventDefault();
        closeDetailDialog();
      }
    }
  };

  const onEsc = (e: KeyboardEvent) => {
    if (e.key !== "Escape") {
      return;
    }
    setTooltipMetadata({ visible: false });
  };

  const onKeyDown = () => (e: KeyboardEvent) => {
    onEsc(e);
    onTab(e);
  };

  const onNodeClick =
    (scale: number, transform: ZoomTransform) =>
    (e: CustomEvent<ServiceTreeEventPayload>) => {
      if (hasInfoTooltip) {
        insertDetailDialog(e, scale, transform);
      }
      setTooltipMetadata({
        visible: false
      });

      onClick(e.detail);
    };

  const nodeRendererCallback = useMemo(() => nodeRenderer(), []);

  return (
    <S.TreeLayout id="treeSvg" strokeColor={strokeColor}>
      <ServiceTree
        id="service-tree"
        maxHeight={graphHeight}
        treePositionY={30}
        nodeRenderer={nodeRendererCallback}
        getNodeDimensions={getNodeDimensions}
        width={width}
        innerRef={innerRef}
        onNodeOver={onNodeOver}
        onNodeOut={onNodeOut}
        onKeyDown={onKeyDown}
        onNodeClick={onNodeClick}
        gridStepX={60}
        gridStepY={80}
        arcLength={10}
        midLayerOffset={40}
        edgeMargin={10}
        onLoadEnd={onLoadEnd}
      />
      {dialogMetadata.visible && (
        <DetailDialog
          metadata={dialogMetadata?.node?.metadata}
          transform={dialogMetadata.position}
          onClick={goToService}
          onClose={() => closeDetailDialog()}
        />
      )}
      {tooltipMetadata.visible && (
        <Tooltip
          metadata={tooltipMetadata?.node?.metadata}
          transform={tooltipMetadata?.position}
        />
      )}
      {isSizingApiEnabled && resources && (
        <S.ResourcesWrapper>
          <Typography.BodyText size="medium" weight="bold">
            {intl.formatMessage(
              {
                id: "apps_services.value_type",
                defaultMessage: `${resources?.cpu} CPU`
              },
              { value: resources?.cpu, type: "CPU" }
            )}
          </Typography.BodyText>
          <Typography.BodyText size="medium" weight="bold">
            {intl.formatMessage(
              {
                id: "apps_services.value_type",
                defaultMessage: `${resources?.ram} GB RAM`
              },
              { value: resources?.ram, type: "GB RAM" }
            )}
          </Typography.BodyText>
          <Typography.BodyText size="medium" weight="bold">
            {intl.formatMessage(
              {
                id: "apps_services.value_type",
                defaultMessage: `${resources?.disk} GB Disk`
              },
              { value: resources?.disk, type: "GB Disk" }
            )}
          </Typography.BodyText>
          <ConfigureResourcesButton
            environmentConfigUrl={getUrl({
              key: "organization.project.environment.configureResources",
              props: {
                organizationId,
                projectId,
                environmentId: environmentId || project?.default_branch || "",
                location: location.pathname + location.search,
                queryParams: ""
              }
            })}
          />
        </S.ResourcesWrapper>
      )}
    </S.TreeLayout>
  );
}

export default ServiceGraph;
